import { PageLoader } from '@ver-uds/react';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AuthServiceConfig, VisAuthService } from '@vis-auth/react';
import { EveRoutes } from '@ver-uds/e-verify';
import { useSecurityContext } from '../../context/Security/SecurityProvider';
import { OTCErrors } from './utils/constants';
import FullWidthLayout from '../../layouts/FullWidthLayout';

const EnrollmentValidateOTC = (): JSX.Element => {
  const [showPageLoader, setShowPageLoader] = useState<boolean>(true);
  const [OTCError, setOTCError] = useState<string>('');

  const { otc } = useParams<{ otc: string }>();
  const authConfig = useSecurityContext();

  // Use the VisAuthService method to handle the OTC validation and auth token swap
  const handleOneTimeCodeLogin = async (code: string, config: AuthServiceConfig): Promise<void> => {
    const visAuthService = new VisAuthService(config);

    // Apply otc to get JWT, set auth state and store token in local storage
    await visAuthService
      .handleOneTimeCodeAuthentication(code)
      .then(() => {
        setShowPageLoader(false);
      })
      .catch((e) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const authErrorCode = JSON.parse(e.message)?.errorList?.[0]?.error ?? OTCErrors.GENERAL_ERROR;
        setOTCError(authErrorCode);
        setShowPageLoader(false);
      });
  };

  // First render will attempt to verify OTC
  useEffect(() => {
    if (authConfig) {
      handleOneTimeCodeLogin(otc, authConfig).catch((e) => {
        setOTCError(OTCErrors.GENERAL_ERROR);
        setShowPageLoader(false);
      });
    } else {
      setOTCError(OTCErrors.CONFIG_NOT_FOUND);
      setShowPageLoader(false);
    }
  }, [authConfig, otc]);

  // This redirect allows the auth token swap to occur
  const OriginRedirect = ({ path }: { path: string }): null => {
    useEffect(() => {
      window.location.assign(path);
    });
    return null;
  };

  // Redirect to public enrollment URL on any OTC auth failure,
  if (OTCError) {
    // Store the returned error code in session storage for Error alert
    sessionStorage.setItem('OTCError', JSON.stringify(OTCError));
    return <OriginRedirect path={EveRoutes.ACCOUNT_ENROLL} />;
  }

  // Redirect to begin account enrollment with OTC authenticated
  if (!showPageLoader && !OTCError) {
    return <OriginRedirect path={EveRoutes.ACCOUNT_ENROLL_NEW} />;
  }

  // Default to loading screen
  return (
    <FullWidthLayout title="Use Agreement" headerTitle="E-Verify Enrollment">
      <div className="margin-y-4">
        <PageLoader label="Loading" sectionOverlay />
      </div>
    </FullWidthLayout>
  );
};

export default EnrollmentValidateOTC;
